import React, { useState, memo, useCallback } from 'react'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals/FormModal'
import { makeStyles } from 'transfix-ui/core/styles'
import { getApolloErrorMessages } from 'transfix-ui/utils/getApolloErrorMessages'
import { IError, Maybe } from 'types/graphqlTypes'
import { useMPAnalytics } from 'apps/marketplace/hooks/useMPAnalytics'
import { isDigitalOption } from 'apps/marketplace/utils/helpers/isDigitalOption'
import CarrierOptionFormBody from './components/CarrierOptionFormBody'
import CarrierOptionFormFooter from './components/CarrierOptionFormFooter'
import { TEST_IDS } from './constants'
import { useCarrierOptionMutations, useCarrierOptionForm } from './hooks'
import { TCarrierOptionSchema } from './schema'
import { ICarrierOption } from './types'
import { getCarrierOptionSchema } from './utils'

const useCarrierOptionFormModalStyles = makeStyles(theme => ({
  modalContainer: {
    width: '100%',
    '& .modal': {
      [theme.breakpoints.up('md')]: {
        width: '95%',
      },
      [theme.breakpoints.up('lg')]: {
        width: '75%',
      },
      [theme.breakpoints.up('xl')]: {
        width: '60%',
      },
    },
  },
  formBody: {
    padding: `${theme.spacing(2)} 0`,
  },
}))

export interface ICarrierOptionFormModalProps extends Pick<IFormModalProps, 'onClose'> {
  shipmentId: string
  carrierOption?: Maybe<ICarrierOption>
  onSuccess: (carrierOptionId: string) => void
}

const CarrierOptionFormModal = memo<ICarrierOptionFormModalProps>(
  ({ carrierOption, onClose, onSuccess, shipmentId }) => {
    const { trackEvent } = useMPAnalytics()
    const classes = useCarrierOptionFormModalStyles()

    const isEditing = Boolean(carrierOption)
    const {
      carrierContactUser,
      counterBid,
      createdBy,
      id: carrierOptionId,
      source: carrierOptionSource,
    } = carrierOption || {}
    const { amount: originalCounterBidAmount } = counterBid || {}
    const isDigital = isDigitalOption(carrierOptionSource)
    const defaultContact = isDigital && !carrierContactUser ? createdBy : carrierContactUser

    const [mutationErrors, setMutationErrors] = useState<IError[]>([])

    // Setting default values with provided option data
    const { control, errors: validationErrors, handleSubmit, setValue } = useCarrierOptionForm({
      context: {
        isSourceFieldsRequired: !isEditing,
      },
      defaultValues: getCarrierOptionSchema(carrierOption),
    })

    const {
      createCarrierOption,
      errors: apolloErrors,
      loading,
      updateCarrierOption,
    } = useCarrierOptionMutations({
      shipmentId,
      carrierOptionId,
    })

    const errors = getApolloErrorMessages({ apolloErrors, mutationErrors })

    const submitHandler = useCallback(
      async (formData: TCarrierOptionSchema) => {
        const {
          counterOfferAmount,
          getApproval: isRequestingApproval,
          nonBookable: isNonBookable,
        } = formData || {}
        const { carrierOptionId, errors = [] } = isEditing
          ? await updateCarrierOption(formData)
          : await createCarrierOption(formData)

        const eventData = { shipmentId, carrierOptionId, formData }

        if (errors.length) {
          trackEvent(
            'Carrier Options',
            isEditing ? 'ERROR_EDITING_OPTION' : 'ERROR_ADDING_OPTION',
            { ...eventData, errors }
          )
          setMutationErrors(errors)
          return
        }

        if (carrierOptionId) {
          trackEvent('Carrier Options', isEditing ? 'EDITED_OPTION' : 'ADDED_OPTION', eventData)
          if (!isEditing) {
            if (isNonBookable) {
              trackEvent('Carrier Options', 'ADDED_NON_BOOKABLE_OPTION', eventData)
            }
            if (isRequestingApproval) {
              trackEvent('Carrier Options', 'REQUESTED_OPTION_APPROVAL', eventData)
            }
            if (counterOfferAmount) {
              trackEvent('Carrier Options', 'ADDED_COUNTER_OFFER', eventData)
            }
          } else if (isEditing) {
            if (!originalCounterBidAmount && counterOfferAmount) {
              trackEvent('Carrier Options', 'ADDED_COUNTER_OFFER', eventData)
            } else if (originalCounterBidAmount && !counterOfferAmount) {
              trackEvent('Carrier Options', 'DELETED_COUNTER_OFFER', eventData)
            } else if (originalCounterBidAmount !== counterOfferAmount) {
              trackEvent('Carrier Options', 'EDITED_COUNTER_OFFER', eventData)
            }
          }
          onSuccess(carrierOptionId)
        }
      },
      [
        createCarrierOption,
        isEditing,
        onSuccess,
        originalCounterBidAmount,
        shipmentId,
        trackEvent,
        updateCarrierOption,
      ]
    )

    return (
      <FormModal
        open
        isClose
        onClose={onClose}
        title={isEditing ? 'Edit Option' : 'Add Option'}
        className={classes.modalContainer}
        dataTest={TEST_IDS.modal}
        error={errors}
        footer={
          <CarrierOptionFormFooter
            control={control}
            isEditing={isEditing}
            loading={loading}
            onCancel={onClose}
            onSubmit={handleSubmit(submitHandler)}
          />
        }>
        <div className={classes.formBody}>
          <CarrierOptionFormBody
            shipmentId={shipmentId}
            control={control}
            setValue={setValue}
            errors={validationErrors}
            isEditMode={isEditing}
            defaultContact={defaultContact}
          />
        </div>
      </FormModal>
    )
  }
)

export default CarrierOptionFormModal
